import React, { useState, useEffect } from 'react';
import { useNavigationGuard } from '../../../../../hooks/useNavigationGuard';
import musicLoader from '../../../../../assets/images/music-loader.gif';
import loaderBackground from '../../../../../assets/images/Background+Blur.svg';
import { trackNavigationEvent, createTrackingPayload } from '../../../../../helper/navigationTracker';

const messages = [
  "Analyzing your music…",
  "Detecting genre…",
  "Detecting sub-genre…",
  "Building custom plugin chain…",
  "Adapting engine to style…",
  "Optimizing stereo field…",
  "Adjusting frequency balance…",
  "Optimizing loudness…",
  "Applying magic touch…",
  "Preparing your preview…",
  "Almost there…"
];

const leaveWarningMessage = "Your processing is still in progress. Leaving this page may interrupt the task.";

const ProcessingLoader = ({ isMaster }) => {
  const [currentMessage, setCurrentMessage] = useState(0);
  const [progress, setProgress] = useState(0);

  // 1. Rotate messages/progress
  useEffect(() => {
    const stepCount = messages.length - 1; // last message is 100%
    const interval = setInterval(() => {
      setCurrentMessage(prev => {
        if (prev < stepCount) {
          const next = prev + 1;
          setProgress(Math.round((next / stepCount) * 100));
          return next;
        } else {
          clearInterval(interval);
          return prev;
        }
      });
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  // 2. Block browser back/forward and app back navigation only
  useNavigationGuard(true, `${leaveWarningMessage} Do you want to leave?`);

  // 3. Warn on browser refresh/close/tab close
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      const payload = createTrackingPayload('page_refresh', {
        taskId: null,
        recordId: null,
        metadata: {
          component: 'MixAnalyst ProcessingLoader',
          isMaster,
        }
      });
      trackNavigationEvent(payload);

      event.preventDefault();
      event.returnValue = leaveWarningMessage;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isMaster]);

  return (
    <div className="bg-white max-w-[700px] mx-auto flex flex-col items-center justify-center text-center gap-6 rounded-2xl p-4 lg:p-5">
      <div className="relative w-48 h-48 mx-auto">
        {/* Background image */}
        {/* <img
          src={loaderBackground}
          className="absolute inset-0 w-full h-full object-contain"
        /> */}

        {/* Foreground / animated image */}
        <img
          src={musicLoader}
          alt="Processing"
          className="absolute inset-0 w-full h-full object-contain"
        />
      </div>
      <div className="w-full max-w-[400px] mx-auto">
        <div className="bg-[#00067e] h-2 rounded-full transition-all duration-500 mb-3" style={{ width: `${progress}%` }} />
        <div className="text-[#FA6138] text-base font-medium">{messages[currentMessage]}</div>
      </div>
      <div className="text-theme_clr2 text-base font-medium opacity-80 leading-relaxed">
        {isMaster
          ? <i>Getting your song ready for release.</i>
          : <i>
            We are analysing your track for technical quality, loudness,<br />
            tone, and stereo balance.
          </i>
        }
      </div>
      <div className="text-sm font-semibold text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
        Please do not refresh, close, or navigate away while processing.
      </div>
    </div>
  );
};

export default ProcessingLoader;
